import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import MarketTicker from './components/MarketTicker';
import ScrollToTop from './components/ScrollToTop';
import CustomCursor from './components/CustomCursor';
import CardEdgeGlow from './components/CardEdgeGlow';
import MagneticButtons from './components/MagneticButtons';
import SiteFooter from './components/SiteFooter';
import About from './pages/About';
import NotFound from './pages/NotFound';
import { I18nProvider } from './hooks/useI18n';
import useHreflang from './hooks/useHreflang';

// Route-level code splitting: the heavy pages (Dashboard pulls in Recharts via
// HistoricalChart) land in their own chunk instead of one bundle carrying
// everything upfront. About is the home page and is NOT split: every visit to
// "/" needs it, so a separate chunk only added a round trip during which the
// page showed an empty <main> with the footer jammed under the navbar.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const StockAnalysis = lazy(() => import('./pages/StockAnalysis'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));

// Old URL kept alive: the home page IS "chi sono" now. Preserves the #section
// anchor so legacy links like /chi-sono#about-contact-section still land right.
function RedirectToHome() {
  const { hash } = useLocation();
  return <Navigate to={{ pathname: '/', hash }} replace />;
}

function AppShell() {
  useHreflang();
  return (
    <>
      <div className="bg-grid" aria-hidden="true" />
      <CustomCursor />
      <CardEdgeGlow />
      <MagneticButtons />
      <ScrollToTop />
      <div className="min-h-screen bg-page">
        <Navbar />
        <main className="pt-16">
          <MarketTicker />
          {/* Full-height placeholder while a lazy page loads, so the footer stays below the fold instead of jumping up under the navbar and back down. */}
          <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
            <Routes>
              <Route path="/" element={<About />} />
              <Route path="/ciclo-economico" element={<Dashboard />} />
              <Route path="/analisi" element={<StockAnalysis />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/condizioni" element={<Terms />} />
              <Route path="/chi-sono" element={<RedirectToHome />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <SiteFooter />
      </div>
    </>
  );
}

function App() {
  // The site used to keep an anonymous visitor id in localStorage (gd_session_id) and ping the server with it
  // every 30s for a live counter. The counter is gone (privacy review, 2026-10-15): nothing identifies a visitor
  // any more, so drop the leftover id from browsers that still carry it.
  useEffect(() => {
    try { localStorage.removeItem('gd_session_id'); } catch (e) { /* storage blocked: nothing to clean */ }
  }, []);

  return (
    <I18nProvider>
      <Router>
        <AppShell />
      </Router>
    </I18nProvider>
  );
}

export default App;
